import { create } from 'zustand';
import type { ProjectSettingsPayloadData } from '@/bridge/features/projectSettings';

interface ProjectSettingsState {
  /** The latest setProjectSettings echo. A FRESH object per echo — SceneBoardPage's
   *  apply-resolution effect distinguishes "the seed I applied from" from "the ack that
   *  just arrived" by object identity alone (values/error can be byte-identical between
   *  the two), so setCurrent must never merge into the existing object. */
  current: ProjectSettingsPayloadData | null;
  /** The stored watermark image as a data URL, or null when the project has none. */
  watermarkPreview: string | null;
  logoPreview: string | null;
  /** A setWatermarkLogoPreviews reply has been received at least once this session. Without it the
   *  tiles cannot tell "this project has no watermark" from "Unity has not answered yet", and would
   *  flash the import affordance for a project that does have one. */
  previewsLoaded: boolean;
  /** projectSettingsPreviewKey(aspectRatioType, sensorSizeType, postProcessingKey, toneMappingMode)
   *  -> data-URL of Unity's rendered preview frame, populated by the setProjectSettingsPreview
   *  bridge handler.
   *
   *  Unlike sceneSettingsStore's sky swatches these ARE dropped by clear(), i.e. on every close: they
   *  are frames of the CURRENT project (its cast, its set, its grade), so carrying them into the next
   *  open would show the previous project's mini scene until Unity answered. The cache exists to make
   *  flipping back and forth between picks free WITHIN one sitting, which it still does. */
  previews: Record<string, string>;
  setCurrent: (d: ProjectSettingsPayloadData) => void;
  setPreviews: (watermark: string | null, logo: string | null) => void;
  clear: () => void;
}

/** Project Settings is a near-copy of sceneSettingsStore minus the delete/mutate slices:
 *  project settings have no destructive row and no context-menu mutations, so `current`
 *  (the echo) plus the watermark/logo previews is the whole surface. The previews live here
 *  rather than on the echo for the same reason sceneSettingsStore holds the floor/sky
 *  swatches: they are answers to their own request verb, arriving on their own schedule. */
export const useProjectSettingsStore = create<ProjectSettingsState>((set) => ({
  current: null,
  watermarkPreview: null,
  logoPreview: null,
  previewsLoaded: false,
  previews: {},
  setCurrent: (d) => set({ current: d }),
  setPreviews: (watermark, logo) => set({ watermarkPreview: watermark, logoPreview: logo, previewsLoaded: true }),
  // Called when the sheet closes. The previews go with it: they are potentially hundreds of KB of
  // base64 that nothing outside the sheet reads, and holding them would also mean the next open
  // shows the previous project's images until its own reply lands. The rendered camera previews are
  // dropped for the same two reasons.
  clear: () => set({ current: null, watermarkPreview: null, logoPreview: null, previewsLoaded: false, previews: {} }),
}));
